/**
 * Control Tower Override Session (P12)
 *
 * Per-user override window. Persists in DB (`control_tower_sessions`).
 * Module keeps a tiny in-memory cache for reactive reads.
 *
 * Activation requires:
 *   • typed CONFIRM
 *   • reason ≥ 10 chars
 *
 * Window: 10 minutes.
 * Rate limit: max 5 override actions per active 10-minute window.
 * Kill switch: cancels session immediately.
 */

import { recordControlTowerAudit } from "./audit";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
export const OVERRIDE_WINDOW_MINUTES = 10;
export const OVERRIDE_RATE_LIMIT = 5;

export interface ControlTowerSession {
  id?: string;
  adminId: string;
  activatedAt: string;
  expiresAt: string;
  reason: string;
  actionCount: number;
}

let sessionCache: ControlTowerSession | null = null;

function setCache(session: ControlTowerSession | null): void {
  sessionCache = session;
}

function mapDbSession(row: Record<string, any>): ControlTowerSession {
  return {
    id: String(row.id ?? ""),
    adminId: String(row.admin_id ?? ""),
    activatedAt: String(row.activated_at ?? new Date().toISOString()),
    expiresAt: String(row.expires_at ?? new Date().toISOString()),
    reason: String(row.reason ?? ""),
    actionCount: Number(row.action_count ?? 0),
  };
}

export async function hydrateControlTowerSession(adminId: string): Promise<ControlTowerSession | null> {
  try {
    const organizationId = await getCurrentOrganizationId().catch(() => null);
    if (!organizationId || !adminId) {
      setCache(null);
      return null;
    }
    const nowIso = new Date().toISOString();
    const { data } = await supabase
      .from("control_tower_sessions")
      .select("*")
      .eq("organization_id", organizationId)
      .eq("admin_id", adminId)
      .is("cancelled_at", null)
      .gt("expires_at", nowIso)
      .order("activated_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    const mapped = data ? mapDbSession(data) : null;
    setCache(mapped);
    return mapped;
  } catch {
    return sessionCache;
  }
}

export function getActiveSession(now: number = Date.now()): ControlTowerSession | null {
  const session = sessionCache;
  if (!session) return null;
  if (new Date(session.expiresAt).getTime() <= now) {
    setCache(null);
    recordControlTowerAudit({
      eventType: "override_mode_expired",
      adminId: session.adminId,
    });
    void persistSessionCancel(session.id ?? null);
    return null;
  }
  return session;
}

export function isOverrideModeActive(now: number = Date.now()): boolean {
  return getActiveSession(now) !== null;
}

export interface ActivateInput {
  adminId: string;
  reason: string;
  confirmText: string;
}

export type ActivateResult =
  | { ok: true; session: ControlTowerSession }
  | { ok: false; reason: "confirm_mismatch" | "reason_too_short" | "missing_admin" };

export function activateOverrideMode(input: ActivateInput): ActivateResult {
  if (!input.adminId) return { ok: false, reason: "missing_admin" };
  if (input.confirmText !== "CONFIRM") return { ok: false, reason: "confirm_mismatch" };
  if ((input.reason ?? "").trim().length < 10) {
    return { ok: false, reason: "reason_too_short" };
  }
  const now = Date.now();
  const session: ControlTowerSession = {
    adminId: input.adminId,
    activatedAt: new Date(now).toISOString(),
    expiresAt: new Date(now + OVERRIDE_WINDOW_MINUTES * 60_000).toISOString(),
    reason: input.reason.trim(),
    actionCount: 0,
  };
  setCache(session);
  void persistSessionActivate(session);
  recordControlTowerAudit({
    eventType: "override_mode_activated",
    adminId: input.adminId,
    reason: session.reason,
  });
  return { ok: true, session };
}

export function cancelOverrideMode(adminId: string, killSwitch = false): void {
  const session = sessionCache;
  setCache(null);
  void persistSessionCancel(session?.id ?? null);
  if (session) {
    recordControlTowerAudit({
      eventType: killSwitch ? "kill_switch_engaged" : "override_mode_cancelled",
      adminId,
    });
  }
}

export type ConsumeResult =
  | { ok: true; session: ControlTowerSession }
  | { ok: false; reason: "not_active" | "rate_limited" };

/**
 * Bump the session's action counter. Returns ok:false if no active session
 * or rate-limit exceeded.
 */
export function consumeOverrideAction(now: number = Date.now()): ConsumeResult {
  const session = getActiveSession(now);
  if (!session) return { ok: false, reason: "not_active" };
  if (session.actionCount >= OVERRIDE_RATE_LIMIT) {
    recordControlTowerAudit({
      eventType: "rate_limit_blocked",
      adminId: session.adminId,
    });
    return { ok: false, reason: "rate_limited" };
  }
  const next: ControlTowerSession = {
    ...session,
    actionCount: session.actionCount + 1,
  };
  setCache(next);
  void persistSessionActionCount(next.id ?? null, next.actionCount);
  return { ok: true, session: next };
}

export function getRemainingMs(now: number = Date.now()): number {
  const session = getActiveSession(now);
  if (!session) return 0;
  return Math.max(0, new Date(session.expiresAt).getTime() - now);
}

/** Test helper. */
export function __resetControlTowerSession(): void {
  setCache(null);
}

async function persistSessionActivate(session: ControlTowerSession): Promise<void> {
  try {
    const organizationId = await getCurrentOrganizationId().catch(() => null);
    if (!organizationId) return;
    const payload = {
      organization_id: organizationId,
      admin_id: session.adminId,
      activated_at: session.activatedAt,
      expires_at: session.expiresAt,
      reason: session.reason,
      action_count: session.actionCount,
      cancelled_at: null,
      updated_at: new Date().toISOString(),
    };
    if (session.id) {
      await supabase.from("control_tower_sessions").update(payload).eq("id", session.id);
      return;
    }
    const { data } = await supabase.from("control_tower_sessions").insert(payload).select("id").maybeSingle();
    if (data?.id) setCache({ ...session, id: data.id as string });
  } catch (error) {
    console.warn("[controlTower.session] activate persist failed", error);
  }
}

async function persistSessionCancel(sessionId: string | null): Promise<void> {
  try {
    if (!sessionId) return;
    await supabase
      .from("control_tower_sessions")
      .update({ cancelled_at: new Date().toISOString(), updated_at: new Date().toISOString() })
      .eq("id", sessionId);
  } catch (error) {
    console.warn("[controlTower.session] cancel persist failed", error);
  }
}

async function persistSessionActionCount(sessionId: string | null, actionCount: number): Promise<void> {
  try {
    if (!sessionId) return;
    await supabase
      .from("control_tower_sessions")
      .update({ action_count: actionCount, updated_at: new Date().toISOString() })
      .eq("id", sessionId);
  } catch (error) {
    console.warn("[controlTower.session] action count persist failed", error);
  }
}
